/**
 * Demo data for a cash table, mid-hand on the turn, with a configurable number
 * of players (1–9). Call `buildTableDemo(seatCount)` to get a payload that feeds
 * straight into the `updateCurrentTable` reducer:
 *
 *   import { updateCurrentTable, updateCurrentPlayerGUID } from 'reducers/currentTable';
 *   import { buildTableDemo, NINE_SEAT_DEMO_POV_GUID } from 'demo/nineSeatTableDemo';
 *
 *   dispatch(updateCurrentPlayerGUID(NINE_SEAT_DEMO_POV_GUID)); // seat 1 = POV
 *   dispatch(updateCurrentTable(buildTableDemo(6)));            // 6-handed table
 *
 * Notes on the data shape (see reducers/currentTable.tsx + components/Table):
 * - `players` is 1-indexed: index 0 is a placeholder that `slice(1)` drops, so
 *   the N occupied seats live at indices 1..N. The seat count drives
 *   useEllipticalPositions, so the oval re-spaces itself for any N.
 * - A seat is treated as occupied when it has a truthy `ID`.
 * - The POV (face-up) seat is the player whose `GUID` matches the user GUID.
 * - Per-seat bets render from `seat.betDisplay`; community/hole cards and action
 *   pills come from the `game.p{N}*` fields.
 *
 * Every opponent is given a live bet so the seat/bet positioning (including the
 * portrait spacing) is exercised at every seat around the oval.
 */

/** GUID of the POV player. Pass to `updateCurrentPlayerGUID`. */
export const NINE_SEAT_DEMO_POV_GUID = 'demo-guid-pov';

/** Game variant the demo can preview (Texas = 2 hole cards, Omaha = 4). */
export type GameVariant = 'texas' | 'omaha';

/** Hole cards per variant. */
const variantHoleCardCount = (isOmaha: boolean) => (isOmaha ? 4 : 2);

/** Slice a full (4-card) hand down to the count the variant uses. */
const cardsForVariant = (full: readonly string[], isOmaha: boolean): string[] =>
  full.slice(0, variantHoleCardCount(isOmaha));

/** Display name + hole cards given to whichever seat is the POV. Texas uses the
 *  first two; Omaha uses all four. */
const POV_NAME = 'You';
const POV_CARDS_FULL = ['AS', 'KS', 'QD', 'QC'] as const;

type DemoSeat = {
  /** Seat number 1..9 */
  seatId: number;
  displayName: string;
  stack: number;
  /** Chips currently in front of the player this betting round (0 = no bet). */
  bet: number;
  /** Engine action for the seat's badge (lowercase, see VALID_ACTIONS), or ''
   *  for no badge. */
  action: string;
  /** Hole cards (2 for Texas, 4 for Omaha). Empty = face-down / no cards. */
  cards?: string[];
};

// Every player is active (in the hand) and has chips in front this round, so
// every seat around the oval shows a bet chip — including the left/right
// portrait seats being tuned. The `buildTableDemo` factory takes the first N of
// these for an N-handed table, then `applyPov` marks the chosen seat as the POV.
const ALL_SEATS: DemoSeat[] = [
  { seatId: 1, displayName: 'Alex', stack: 12_500, bet: 600, action: '' },
  { seatId: 2, displayName: 'Bryce', stack: 8_300, bet: 600, action: '' },
  { seatId: 3, displayName: 'Carla', stack: 21_750, bet: 600, action: '' },
  { seatId: 4, displayName: 'Diego', stack: 4_120, bet: 600, action: '' },
  { seatId: 5, displayName: 'Esme', stack: 33_000, bet: 600, action: '' },
  { seatId: 6, displayName: 'Frank', stack: 9_980, bet: 600, action: '' },
  { seatId: 7, displayName: 'Greta', stack: 15_400, bet: 600, action: '' },
  { seatId: 8, displayName: 'Hugo', stack: 2_650, bet: 600, action: '' },
  { seatId: 9, displayName: 'Ivy', stack: 18_220, bet: 600, action: '' }
];

/** Full river board (all 5 community cards). Pairs the POV's A♠K♠ into two
 *  pair, so the displayed hand rank is consistent with the cards. */
const BOARD = ['AH', 'KH', '2D', 'QS', '7C'] as const;

/** Betting round / community-card stage the demo can preview. */
export type BoardStage = 'preflop' | 'flop' | 'turn' | 'river';

// `game.hand` value per stage. Pre-flop uses 1 (ADJUST_BLINDS) on purpose:
// <Holdem Cards/> only clears a previously-dealt board when the hand resets to
// <= 1, so 1 is what actually removes all community cards (flop/turn/river show
// the right count via the empty card slots below).
const STAGE_HAND: Record<BoardStage, string> = {
  preflop: '1', // GAME_HAND.ADJUST_BLINDS
  flop: '7', // GAME_HAND.FLOP_BETTING_ROUND
  turn: '9', // GAME_HAND.TURN_BETTING_ROUND
  river: '11' // GAME_HAND.RIVER_BETTING_ROUND
};

/** Number of community cards shown at each stage. */
const STAGE_BOARD_COUNT: Record<BoardStage, number> = {
  preflop: 0,
  flop: 3,
  turn: 4,
  river: 5
};

/** Run-it-twice: how many boards the hand is run out on (0 = feature off). */
export type MultiRunCount = 0 | 2 | 3;

/** Run counts the demo control offers (beyond "off"). Run-it-twice caps at 3. */
export const MULTI_RUN_COUNTS: MultiRunCount[] = [2, 3];

/** The street the all-in was declared on — decides how much board is shared. */
export type MultiRunTrigger = 'preflop' | 'flop' | 'turn';

export const MULTI_RUN_TRIGGERS: MultiRunTrigger[] = ['preflop', 'flop', 'turn'];

/**
 * `game.runs` = "<totalRuns>,<stage>,<currentRun>", where `stage` is the
 * `game.hand` the all-in happened on. Both the engine (runMulti.RunMultiple)
 * and the client (<HoldEmCards/> sharedCardCount) map that stage to the number
 * of community cards shared by every run, so the demo has to use the same
 * values:
 *   pre-flop ('5')  → nothing shared, all 5 cards re-dealt per run
 *   flop     ('7')  → flop shared, turn + river re-dealt
 *   turn     ('9')  → flop + turn shared, river re-dealt
 */
const MULTI_RUN_TRIGGER: Record<MultiRunTrigger, { hand: string; sharedCards: number }> = {
  preflop: { hand: '5', sharedCards: 0 },
  flop: { hand: '7', sharedCards: 3 },
  turn: { hand: '9', sharedCards: 4 }
};

/**
 * The full board dealt on each run. Run 1 reuses the single-board demo board;
 * runs 2 and 3 use cards found nowhere else on the felt so every branch reads
 * differently. Only the cards from the all-in street onward are used — the
 * prefix always comes from `BOARD` (it was dealt before the boards forked).
 */
const RUN_BOARDS: Record<number, readonly string[]> = {
  1: BOARD,
  2: ['9H', '4C', '8S', '3H', '5D'],
  3: ['5C', 'JH', '6D', '2H', 'QH']
};

/**
 * Run-it-twice demo controls.
 *
 * `progress` is 1-based over the run-out: 1..`runs` means that run is the one
 * currently on the felt (every earlier run has settled), and `runs + 1` is the
 * final state where the last board has been paid out too. 0 is the step BEFORE
 * the run-out: the vote window, still open, with no board dealt yet.
 */
export type MultiRunConfig = {
  runs: MultiRunCount;
  trigger: MultiRunTrigger;
  progress: number;
};

/**
 * `game.runs` while the vote window is open, in the form the voteMultiRun
 * handler writes: `v<votedSeats>/<vote>/<twice>/<thrice>`. This is the seeded
 * value — nobody has voted yet, so segment 1 is '0' and <MultiRunActions/>
 * shows the neutral offer with all three buttons. Exported because the demo
 * page hands the same string to that panel.
 */
export const MULTI_RUN_VOTE_RUNS = 'v0/0/0/0';

export const DEFAULT_MULTI_RUN: MultiRunConfig = {
  runs: 0,
  trigger: 'flop',
  progress: 1
};

/** Per-knockout bounty value (chips). The badge only shows the COUNT, so the
 *  exact amount just needs `totalBounty` to be a clean multiple of it. */
const BOUNTY_AMOUNT = 100;

/** Knockouts a seat shows when bounty mode is on and none is set explicitly. */
export const DEFAULT_SEAT_KNOCKOUTS = 1;

/** Largest knockout count the demo control cycles to. */
export const MAX_DEMO_KNOCKOUTS = 5;

/**
 * Per-seat action badge (the engine's lowercase `p{N}action` values). `none`
 * shows no badge — the default, so existing stories render unchanged. Fold is
 * driven by the `fold` card mode instead, since it also removes the cards.
 */
export type SeatActionMode = 'none' | 'check' | 'call' | 'bet' | 'raise' | 'allin';

/**
 * Per-seat card presentation for opponents (the POV is always its own face-up):
 * - `down`: holding face-down cards (in the hand) — the default.
 * - `show`: cards revealed face-up (showdown reveal).
 * - `fold`: folded — no cards, FOLD action pill, no bet chip.
 * - `none`: seated with no cards rendered (e.g. not dealt in).
 */
export type SeatCardMode = 'down' | 'show' | 'fold' | 'none';

/** Default card mode for an opponent seat when none is specified. */
export const DEFAULT_SEAT_CARD_MODE: SeatCardMode = 'down';

/** Hole cards revealed for a `show` opponent seat (distinct per seat). Four
 *  cards each; Texas uses the first two, Omaha all four. */
const OPPONENT_SHOWN_CARDS: Record<number, string[]> = {
  1: ['TS', 'TD', 'TH', 'TC'],
  2: ['9S', '9D', '9H', '9C'],
  3: ['JS', 'JD', 'JH', 'JC'],
  4: ['8S', '8D', '8H', '8C'],
  5: ['6S', '6D', '6H', '6C'],
  6: ['5S', '5D', '5H', '5C'],
  7: ['4S', '4D', '4H', '4C'],
  8: ['3S', '3D', '3H', '3C'],
  9: ['7S', '7H', '7D', '2S']
};

/**
 * Mark the seat at `povSeat` as the POV: give it the "You" name and face-up
 * hole cards (2 for Texas, 4 for Omaha). Other seats stay opponents.
 */
function applyPov(seats: DemoSeat[], povSeat: number, isOmaha: boolean): DemoSeat[] {
  return seats.map((s) =>
    s.seatId === povSeat
      ? { ...s, displayName: POV_NAME, cards: cardsForVariant(POV_CARDS_FULL, isOmaha) }
      : s
  );
}

/**
 * Resolve a seat's effective card mode. The POV only honours `fold` / `none`
 * (your own cards are otherwise always face-up), so anything else maps to
 * `show`; opponents default to face-down.
 */
const seatCardMode = (
  seatId: number,
  povSeat: number,
  modes: Record<number, SeatCardMode>
): SeatCardMode => {
  if (seatId === povSeat) {
    const mode = modes[seatId];
    return mode === 'fold' || mode === 'none' ? mode : 'show';
  }
  return modes[seatId] ?? DEFAULT_SEAT_CARD_MODE;
};

/**
 * Apply per-seat card modes. `show` reveals cards (the POV keeps its own face-up
 * hand from `applyPov`; opponents get revealed values). `fold` sets the FOLD
 * action and forfeits the bet chip — the POV keeps its cards so they animate
 * away as folded, opponents simply have none. `down` / `none` carry no card
 * values (their backs/visibility are decided by `seatsWithVisibleCards`).
 */
function applySeatModes(
  seats: DemoSeat[],
  modes: Record<number, SeatCardMode>,
  povSeat: number,
  isOmaha: boolean
): DemoSeat[] {
  return seats.map((s) => {
    const isPov = s.seatId === povSeat;
    const mode = seatCardMode(s.seatId, povSeat, modes);
    if (mode === 'fold') {
      return { ...s, action: 'fold', bet: 0, cards: isPov ? s.cards : undefined };
    }
    if (mode === 'show') {
      return isPov ? s : { ...s, cards: cardsForVariant(OPPONENT_SHOWN_CARDS[s.seatId], isOmaha) };
    }
    return { ...s, cards: undefined };
  });
}

/**
 * Apply per-seat action badges. `check` puts no chips in (no bet chip) and
 * `allin` empties the stack; the rest keep the seat's bet. Folded seats keep
 * their FOLD badge — the card mode wins.
 */
function applySeatActions(seats: DemoSeat[], actions: Record<number, SeatActionMode>): DemoSeat[] {
  return seats.map((s) => {
    const action = actions[s.seatId] ?? 'none';
    if (s.action === 'fold' || action === 'none') return s;
    if (action === 'check') return { ...s, action, bet: 0 };
    if (action === 'allin') return { ...s, action, stack: 0 };
    return { ...s, action };
  });
}

/** Build the per-seat `game.p{N}*` fields from the demo seat list. */
function buildSeatGameFields(seats: DemoSeat[]): Record<string, any> {
  const fields: Record<string, any> = {};
  for (const s of seats) {
    const folded = s.action === 'fold';
    fields[`p${s.seatId}bet`] = folded ? 'F' : `B${s.bet}`;
    fields[`p${s.seatId}lbet`] = s.bet;
    fields[`p${s.seatId}action`] = s.action;
    // PLAYER_STATUS.ACTIVE = 0. (1 = SITTING_OUT, which renders the overlay.)
    fields[`p${s.seatId}status`] = 0;
    // Always emit all four hole-card slots — the reducer MERGES game payloads,
    // so slots a seat no longer uses (revealed → face-down, or Omaha → Texas)
    // must be explicitly cleared to '' or the stale cards persist. Empty values
    // are filtered out and render as face-down backs.
    const cards = s.cards ?? [];
    for (let i = 0; i < 4; i++) {
      fields[`p${s.seatId}card${i + 1}`] = cards[i] ?? '';
    }
  }
  return fields;
}

/** An empty seat object — rendered as an OpenSeat (no truthy `ID`). */
const emptySeat = () => ({
  GUID: '',
  ID: '',
  stack: 0,
  bet: '',
  lbet: 0,
  betDisplay: '',
  action: '',
  status: 0,
  openAddFunds: false,
  totalBounty: 0,
  totalHandBounty: 0,
  isMuted: 0,
  displayName: '',
  duration: '',
  avatar: ''
});

/**
 * Build the 1-indexed `players` array for an N-seat table (index 0 is the
 * dropped placeholder). `seats` holds only the occupied seats keyed by their
 * `seatId`; any seat id in 1..N without an entry renders as an open seat, so the
 * open seats keep their real position around the oval. The seat at `povSeat`
 * gets the POV GUID so the reducer derives it as the POV.
 */
function buildPlayers(
  seats: DemoSeat[],
  n: number,
  povSeat = 1,
  bounties: Record<number, number> = {}
): any[] {
  const occupiedById = new Map(seats.map((s) => [s.seatId, s]));
  const players: any[] = [emptySeat()];
  for (let id = 1; id <= n; id++) {
    const s = occupiedById.get(id);
    if (!s) {
      players.push(emptySeat());
      continue;
    }
    players.push({
      GUID: s.seatId === povSeat ? NINE_SEAT_DEMO_POV_GUID : `demo-guid-seat-${s.seatId}`,
      ID: s.seatId,
      stack: s.stack,
      bet: s.action === 'fold' ? 'F' : `B${s.bet}`,
      lbet: s.bet,
      betDisplay: s.bet > 0 ? String(s.bet) : '0',
      action: s.action,
      status: 0, // PLAYER_STATUS.ACTIVE (1 = SITTING_OUT)
      openAddFunds: false,
      totalBounty: bounties[s.seatId] ?? 0,
      totalHandBounty: 0,
      isMuted: 0,
      displayName: s.displayName,
      duration: '',
      // SeatWithPlayer prepends `https://media.<domain>/images/`, so this is
      // just the filename. Each seat gets a different numbered saloon avatar.
      avatar: `${String(s.seatId).padStart(3, '0')}-saloon.png`
    });
  }
  return players;
}

/** Largest demo table available. */
export const MAX_DEMO_SEATS = ALL_SEATS.length;

/**
 * Bet presets for the demo page's Bet control: the chips every active seat has
 * in front of it, in dollars before the currency scale. The larger ones exist to
 * preview how the bet pill grows with the amount ("$1,000,000" is much wider
 * than "$100").
 *
 * Only the page applies these. `buildTableDemo` without a `betAmount` keeps the
 * fixture's own 600 — the PokerActions stories hard-code call/raise amounts
 * against it.
 */
export const DEMO_BET_AMOUNTS = [100, 10_000, 100_000, 1_000_000, 10_000_000] as const;
export const DEFAULT_DEMO_BET_AMOUNT = DEMO_BET_AMOUNTS[0];

/** Pot layouts the demo can preview (drives the <Pot/> component). */
/**
 * `win` is the pot paid to one player, as a real showdown ends: the streak
 * play's winner step (it is not on the Pot controls, which keep `winnings`
 * for checking the label on every seat at once).
 */
export type PotScenario = 'main' | 'side' | 'showdown' | 'winnings' | 'win';

/**
 * The five cards the demo's winning hand is made of — the strip the winner
 * banner draws under the hand name. Five is the widest the banner ever gets, so
 * it is also the case its clearance from the pot has to hold for.
 */
const DEMO_WINNING_CARDS = ['10S', '10D', 'QC', 'QH', '8S'];

/** Max side pots the demo can preview. */
export const MAX_DEMO_SIDE_POTS = 7;

/** Money display mode for the demo (both are cash `$` tables). */
export type Currency = 'dollars' | 'cents';

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Scale a seat's money values for the chosen currency. */
const scaleSeat = (s: DemoSeat, scale: number): DemoSeat => ({
  ...s,
  stack: round2(s.stack * scale),
  bet: round2(s.bet * scale)
});

/** Build the pot-related game/gameState fields for the chosen scenario. */
function buildPotState(
  seats: DemoSeat[],
  scenario: PotScenario,
  sidePotCount: number,
  moneyScale: number,
  povSeat: number
) {
  // `seats` are already money-scaled; scale the standalone pot constants too.
  const totalBets = seats.reduce((sum, s) => sum + s.bet, 0);
  const main = round2(2_400 * moneyScale + totalBets);
  const allPlayers = seats.map((s) => s.seatId);

  if (scenario === 'side') {
    // Main pot + N side pots — shows the MP / SP(1) … SP(N) layout.
    const count = Math.max(1, Math.min(MAX_DEMO_SIDE_POTS, Math.floor(sidePotCount)));
    const sidePots = Array.from({ length: count }, (_, i) => ({
      pot: round2(Math.max(50, 600 - i * 75) * moneyScale),
      players: allPlayers.slice(0, Math.max(2, allPlayers.length - i)),
      winners: [] as any[],
      label: `SP(${i + 1})`
    }));
    return {
      pots: [{ pot: main, players: allPlayers, winners: [] as any[], label: 'MP' }, ...sidePots],
      showdown: { currentPot: 0, currentStep: 0, handType: '' },
      isShowdown: false
    };
  }

  if (scenario === 'showdown') {
    // A labelled pot being awarded to the POV (currentStep < 3 keeps the amount).
    // Contest it between the winner and one other occupied seat. With no POV
    // (spectator), award the first occupied seat instead.
    const winnerSeat = povSeat > 0 ? povSeat : allPlayers[0] ?? 0;
    const opponent = allPlayers.find((id) => id !== winnerSeat) ?? winnerSeat;
    return {
      pots: [
        {
          pot: main,
          players: [winnerSeat, opponent],
          winners: [{ player: String(winnerSeat), cards: '', hand: '', win: main, handType: '' }],
          label: 'Main Pot'
        }
      ],
      showdown: { currentPot: 0, currentStep: 1, handType: '' },
      isShowdown: true
    };
  }

  if (scenario === 'win') {
    // The showdown's pot, paid (currentStep 3) to its one winner, with the
    // hand text and cards that put the winning-hand banner up.
    const winnerSeat = povSeat > 0 ? povSeat : allPlayers[0] ?? 0;
    const opponent = allPlayers.find((id) => id !== winnerSeat) ?? winnerSeat;
    return {
      pots: [
        {
          pot: main,
          players: [winnerSeat, opponent],
          winners: [
            {
              player: String(winnerSeat),
              cards: DEMO_WINNING_CARDS.join(','),
              hand: `TWO PAIR (${DEMO_WINNING_CARDS.join(',')})`,
              win: main,
              handType: ''
            }
          ],
          label: 'Main Pot'
        }
      ],
      showdown: { currentPot: 0, currentStep: 3, handType: '' },
      isShowdown: true
    };
  }

  if (scenario === 'winnings') {
    // Pot AWARDED (currentStep 3 = chips paid out) so playerPotWin > 0 and the
    // "+ $amount" winnings label renders. Every occupied seat is made a winner
    // so the label can be checked against the stack on ALL seat positions at
    // once (landscape overlap fix).
    const perSeatWin = round2(main / Math.max(1, allPlayers.length));
    return {
      pots: [
        {
          pot: main,
          players: allPlayers,
          // The first winner carries the hand text + its five cards, which is
          // what puts the winning-hand banner on the felt (potWinningHand /
          // potWinningCards take the first winner that has one). Without it the
          // demo could preview every felt element EXCEPT the banner.
          winners: allPlayers.map((seat, i) => ({
            player: String(seat),
            cards: i === 0 ? DEMO_WINNING_CARDS.join(',') : '',
            hand: i === 0 ? `TWO PAIR (${DEMO_WINNING_CARDS.join(',')})` : '',
            win: perSeatWin,
            handType: ''
          })),
          label: 'Main Pot'
        }
      ],
      showdown: { currentPot: 0, currentStep: 3, handType: '' },
      isShowdown: true
    };
  }

  // Default: one main pot — shows the central POT pill.
  return {
    pots: [{ pot: main, players: allPlayers, winners: [] as any[], label: 'POT' }],
    showdown: { currentPot: 0, currentStep: 0, handType: '' },
    isShowdown: false
  };
}

/** One run's full board: the prefix shared by every run, then its own tail. */
const runBoard = (run: number, sharedCards: number): string[] => [
  ...BOARD.slice(0, sharedCards),
  ...(RUN_BOARDS[run] ?? BOARD).slice(sharedCards)
];

/** The `runWinners` shape the reducer starts from — nothing has settled yet. */
const emptyRunWinners = () => ({
  run1: { winners: [] as number[], winTypes: [] as string[] },
  run2: { winners: [] as number[], winTypes: [] as string[] },
  run3: { winners: [] as number[], winTypes: [] as string[] }
});

/**
 * Seats that can be shown taking a board: everyone still in the hand, POV first
 * so the branch it wins carries the green "you won this run" highlight.
 */
const multiRunWinnerSeats = (seats: DemoSeat[], povSeat: number): number[] => {
  const contenders = seats.filter((s) => s.action !== 'fold').map((s) => s.seatId);
  const pool = contenders.length > 0 ? contenders : seats.map((s) => s.seatId);
  return pool.includes(povSeat) ? [povSeat, ...pool.filter((id) => id !== povSeat)] : pool;
};

/** The run-it-twice fields the client reads, plus the live board/hand they imply. */
type MultiRunState = {
  /** `game.runs` — "<totalRuns>,<stage>,<currentRun>", or the voting form. */
  runs: string;
  /** `game.hand` for this point in the run-out. */
  hand: string;
  /** `card1`..`card5` — the board of the run currently being dealt. */
  board: string[];
  multiRunCards: Record<string, string[]>;
  runWinners: Record<string, any>;
};

/**
 * Build the run-it-twice payload exactly as cash-game-broadcast assembles it, so
 * the demo exercises the same code paths as a live multi-run hand:
 *
 * - the run being dealt carries the FULL live board (`card1`..`card5`), while
 *   runs that already settled carry only their divergent tail — the engine
 *   stores that tail in `usedCards` and the broadcaster back-fills from it;
 * - runs that haven't been dealt yet are absent, so no branch renders for them;
 * - `runWinners.totalPot` is the whole-hand pot captured at the all-in (the
 *   live pots are drained by each run's settlement), and each settled run
 *   reports what it actually paid out via `potWon` + `details`.
 *
 * Returns null when the hand isn't being run multiple times.
 */
function buildMultiRunState(
  config: MultiRunConfig,
  seats: DemoSeat[],
  povSeat: number,
  totalPot: number
): MultiRunState | null {
  const totalRuns = Math.floor(config.runs);
  if (totalRuns < 2) return null;

  const { hand: triggerHand, sharedCards } = MULTI_RUN_TRIGGER[config.trigger];

  // Step 0 — the vote window, before any board is run out. `runs` carries the
  // voting form the engine opens the window with, which has no commas: the
  // reducer reads isMultiRunActive as false, exactly as it does live until the
  // votes are in and the engine rewrites runs to the run-out form.
  //
  // The felt still sits at the all-in street, so the hand comes from STAGE_HAND
  // and NOT from the trigger's run-out stage ('5' pre-flop): only a hand of <= 1
  // makes <HoldEmCards/> drop a board it has already dealt, which is what
  // stepping back to a pre-flop vote from a run-out has to do.
  if (Math.floor(config.progress) <= 0) {
    return {
      runs: MULTI_RUN_VOTE_RUNS,
      hand: STAGE_HAND[config.trigger],
      board: BOARD.map((card, i) => (i < sharedCards ? card : '')),
      multiRunCards: {},
      runWinners: emptyRunWinners()
    };
  }

  const progress = Math.max(1, Math.min(totalRuns + 1, Math.floor(config.progress)));
  // The run on the felt, and how many boards behind it have been paid out.
  const currentRun = Math.min(progress, totalRuns);
  const settledRuns = progress - 1;

  // EVERY board, not just the ones paid out: runMulti.PreDealRuns deals the
  // whole set at run 1's showdown and the broadcaster sends them all from the
  // first payout onward (gameTransforms.js, "Boards are pre-dealt"). Filling
  // only up to `currentRun` made the demo a row short of the live felt at every
  // step but the last — and the felt's layout (fork scale, which row the pot
  // pill and the winner banner ride) is decided by that row count.
  const multiRunCards: Record<string, string[]> = {};
  for (let run = 1; run <= totalRuns; run++) {
    const board = runBoard(run, sharedCards);
    multiRunCards[`run${run}`] = run === currentRun ? board : board.slice(sharedCards);
  }

  // Every board contests an equal slice of the pot captured at the all-in.
  const potPerRun = round2(totalPot / totalRuns);
  const winnerSeats = multiRunWinnerSeats(seats, povSeat);
  const runWinners: Record<string, any> = { ...emptyRunWinners(), totalPot };
  for (let run = 1; run <= settledRuns; run++) {
    const seat = winnerSeats[(run - 1) % winnerSeats.length];
    runWinners[`run${run}`] = {
      winners: [seat],
      winTypes: ['win'],
      potWon: potPerRun,
      details: [
        {
          seat,
          name: seats.find((s) => s.seatId === seat)?.displayName ?? '',
          win: potPerRun,
          hand: 'Two Pair'
        }
      ]
    };
  }

  return {
    runs: `${totalRuns},${triggerHand},${currentRun}`,
    // Each board is run out to the river; the hand only reaches FIND_WINNERS
    // once the last one has been paid out.
    hand: settledRuns >= totalRuns ? '13' : '11',
    board: runBoard(currentRun, sharedCards),
    multiRunCards,
    runWinners
  };
}

/** Resolved seat layout: which seats are occupied, which are open, and the POV. */
export type DemoSeatLayout = {
  /** Total seats around the table (clamped 1..9). */
  n: number;
  /** POV seat id, or 0 for spectator (no POV). */
  pov: number;
  /** Seat ids rendered as open (empty) seats. */
  openSet: Set<number>;
  /** Occupied seat ids in ascending order. */
  occupiedIds: number[];
};

/**
 * Resolve the seat layout for the given controls. Shared by `buildTableDemo`
 * and the demo UI so both agree on which seats are occupied / open / the POV.
 */
export function getDemoSeatLayout(
  seatCount: number,
  openSeatCount = 0,
  povSeat = 1,
  povAssigned = true
): DemoSeatLayout {
  const n = Math.max(1, Math.min(MAX_DEMO_SEATS, Math.floor(seatCount)));
  // Leave `openCount` seats empty (rendered as open seats); keep at least the
  // POV occupied.
  const openCount = Math.max(0, Math.min(n - 1, Math.floor(openSeatCount)));
  const chosenSeat = Math.max(1, Math.min(n, Math.floor(povSeat)));
  // When assigned, the POV occupies `chosenSeat`; when not, there is no POV
  // (spectator) and that seat is vacated — rendered as an open seat.
  const pov = povAssigned ? chosenSeat : 0;
  const vacated = povAssigned ? 0 : chosenSeat;
  // Open the highest-numbered seats first, but never the POV. The vacated POV
  // seat is always open and counts on top of the requested open seats.
  const openSet = new Set<number>();
  if (vacated) openSet.add(vacated);
  const targetOpen = Math.min(n, openCount + (vacated ? 1 : 0));
  for (let id = n; id >= 1 && openSet.size < targetOpen; id--) {
    if (id !== pov && id !== vacated) openSet.add(id);
  }
  const occupiedIds = Array.from({ length: n }, (_, i) => i + 1).filter((id) => !openSet.has(id));
  return { n, pov, openSet, occupiedIds };
}

/**
 * Build a demo `updateCurrentTable` payload for an N-handed table (clamped to
 * 1..9) and a pot scenario. `povSeat` chooses which seat (clamped to 1..n) the
 * POV sits on; it is always kept seated. Set `povAssigned` to false for no POV
 * (spectator view — no face-up cards, an unseated viewer, and the chosen
 * `povSeat` rendered as an open seat). `seatCardModes` controls each opponent
 * seat's cards (face-down / shown / folded / none) and `seatActions` its action
 * badge (none by default). `turnSeat` picks whose turn it is (0 = the demo's
 * default seat; an unoccupied seat falls back to it). `betAmount` overrides
 * the chips every active seat has in front of it (default: the fixture's 600).
 * The seat count flows into useEllipticalPositions, so the table re-spaces
 * itself for any N. `multiRun` runs the hand out on 2–3 boards; while it is on
 * it owns the community cards, so `boardStage` no longer applies.
 */
export function buildTableDemo(
  seatCount: number,
  potScenario: PotScenario = 'main',
  sidePotCount = 2,
  currency: Currency = 'dollars',
  openSeatCount = 0,
  povSeat = 1,
  povAssigned = true,
  seatCardModes: Record<number, SeatCardMode> = {},
  boardStage: BoardStage = 'river',
  bountyOn = false,
  seatKnockouts: Record<number, number> = {},
  variant: GameVariant = 'texas',
  straddleSeat = 0,
  multiRunConfig: MultiRunConfig = DEFAULT_MULTI_RUN,
  seatActions: Record<number, SeatActionMode> = {},
  turnSeat = 0,
  betAmount?: number
) {
  const isOmaha = variant === 'omaha';
  // hasStraddle = "flag|seat". Always flag the seat (even 0) so the reducer
  // updates the latched indicator rather than keeping a stale one; seat 0 (or
  // any unoccupied seat) matches no rendered seat, so it reads as "no straddle".
  // The badge only shows pre-flop — the reducer clears it on the flop onward.
  const straddle = `1|${Math.max(0, Math.floor(straddleSeat))}`;
  const { n, pov, occupiedIds } = getDemoSeatLayout(seatCount, openSeatCount, povSeat, povAssigned);
  // Community cards shown for the chosen stage; slots past the stage are cleared
  // to '' so the merging reducer drops a previously-dealt later street.
  const boardCount = STAGE_BOARD_COUNT[boardStage];
  const board = BOARD.map((card, i) => (i < boardCount ? card : ''));
  // Bounty knockouts → each occupied seat's `totalBounty` (a multiple of the
  // bounty amount; the badge shows the resulting count). Off = no bounty game.
  const bountyAmount = bountyOn ? BOUNTY_AMOUNT : 0;
  const bounties: Record<number, number> = {};
  if (bountyOn) {
    for (const id of occupiedIds) {
      const knockouts = seatKnockouts[id] ?? DEFAULT_SEAT_KNOCKOUTS;
      bounties[id] = Math.max(0, knockouts) * bountyAmount;
    }
  }
  // Both modes are cash ($) tables; cents just scales the amounts down so the
  // stakes read like a penny table ($0.02/$0.05) instead of a dollar table.
  const moneyScale = currency === 'cents' ? 0.0001 : 1;
  const seats = applySeatActions(
    applySeatModes(
      applyPov(
        occupiedIds.map((id) =>
          scaleSeat({ ...ALL_SEATS[id - 1], bet: betAmount ?? ALL_SEATS[id - 1].bet }, moneyScale)
        ),
        pov,
        isOmaha
      ),
      seatCardModes,
      pov,
      isOmaha
    ),
    seatActions
  );
  // Cards are visible (backs or revealed) for seats in `down`/`show`; the POV
  // always shows its own. `show` seats additionally reveal their values.
  const seatsWithVisibleCards = occupiedIds.filter((id) => {
    const mode = seatCardMode(id, pov, seatCardModes);
    return mode === 'down' || mode === 'show';
  });
  const seatsWithShowCard = occupiedIds.filter(
    (id) => id !== pov && seatCardMode(id, pov, seatCardModes) === 'show'
  );
  const totalBets = seats.reduce((sum, s) => sum + s.bet, 0);
  const potTotal = round2(2_400 * moneyScale + totalBets);
  const pot = buildPotState(seats, potScenario, sidePotCount, moneyScale, pov);
  // Run-it-twice takes over the community cards: the board on the felt is the
  // run being dealt, and the hand sits in the run-out rather than at a stage the
  // Board control picked.
  const multiRun = buildMultiRunState(multiRunConfig, seats, pov, potTotal);
  const liveBoard = multiRun?.board ?? board;
  const sb = currency === 'cents' ? 0.02 : 50;
  const bb = currency === 'cents' ? 0.05 : 100;
  // Pin dealer / blinds / action to occupied seats (open seats may now fall
  // anywhere, so index into the occupied list rather than fixed seat numbers).
  const pickOccupied = (i: number) => occupiedIds[Math.min(i, occupiedIds.length - 1)];
  const dealerSeat = pickOccupied(0);
  const sbSeat = pickOccupied(1);
  const bbSeat = pickOccupied(2);
  const moveSeat = occupiedIds.includes(turnSeat) ? turnSeat : pickOccupied(4);

  return {
    game: {
      // Cash ($) table; betting round set by the chosen board stage.
      tabletype: 's',
      // 'Bounty' enables the per-seat knockout badge (<BountyBullets/>).
      gameType: bountyOn ? 'Bounty' : '',
      bountyAmount,
      // Variant: 'omaha' makes the reducer set isOmaha → 4 hole cards.
      game: isOmaha ? 'omaha' : 'holdem',
      hand: multiRun?.hand ?? STAGE_HAND[boardStage],
      sb,
      bb,
      // Cash-table stakes label shown by <TableDetails/> (it renders
      // game.blindLevels when tabletype === 's').
      blindLevels: `$${sb}/$${bb} NLH`,
      sbPlayer: String(sbSeat),
      bbPlayer: String(bbSeat),
      dealer: String(dealerSeat),
      dealerVisible: true,
      move: String(moveSeat), // seat whose turn it is
      pot: potTotal,
      totalPot: potTotal,
      // Pot pill data — the <Pot/> component renders from these. The shape
      // (single / side pots / showdown) is chosen by potScenario.
      pots: pot.pots,
      showdown: pot.showdown,
      deadMoney: 0,
      // Community cards — only those dealt for the chosen stage are populated
      // (or, while running it twice, the board of the run being dealt).
      card1: liveBoard[0],
      card2: liveBoard[1],
      card3: liveBoard[2],
      card4: liveBoard[3],
      card5: liveBoard[4],
      // Run-it-twice: "<totalRuns>,<stage>,<currentRun>" while boards are being
      // run out, empty otherwise. The reducer derives isMultiRunActive from it,
      // so it must be sent every time (game payloads are merged, not replaced).
      runs: multiRun?.runs ?? '',
      multiRunCards: multiRun?.multiRunCards ?? {},
      runWinners: multiRun?.runWinners ?? emptyRunWinners(),
      // Seats holding cards (face-down backs or revealed); see seatCardModes.
      seatsWithVisibleCards: seatsWithVisibleCards as number[],
      seatsWithShowCard: seatsWithShowCard as number[],
      seatsWithMuckCard: [] as number[],
      // Required by the reducer (it reads game.chatMessages.length directly).
      chatMessages: [] as any[],
      winner: '',
      overBet: '',
      hasStraddle: straddle,
      // POV hand rank — shown under the current player's seat via <HandRank/>.
      // No board, no made hand to show. Read off the felt rather than the Board
      // control, because multi-run owns the community cards while it is on: a
      // run-out is always at the river, a vote window at the all-in street.
      handRank: liveBoard[0] ? 'Two Pair' : '',
      msg: `demo-${n}-seat-table`,
      ...buildSeatGameFields(seats)
    } as Record<string, any>,
    gameState: {
      startTurn: true,
      preDeal: false,
      isShowdown: pot.isShowdown
    } as Record<string, any>,
    players: buildPlayers(seats, n, pov, bounties),
    ts: 0
  };
}

type DemoTablePayload = ReturnType<typeof buildTableDemo>;

/**
 * Add the POV's `gameState.pokerActions` — the options the engine broadcasts
 * for the seated player, which drive the real action bar (PokerActions):
 * Fold / Call-or-Check / Bet-or-Raise on the POV's turn, the pre-action
 * checkboxes otherwise. Derived from the payload itself (amount to call, stack,
 * bets on the table) the way the engine would, so the bar matches the felt.
 *
 * Opt-in rather than part of buildTableDemo: stories that mount the footer rely
 * on the reducer's empty default (no action row) or bake their own.
 */
export function withDemoPokerActions(table: DemoTablePayload): DemoTablePayload {
  const seats = table.players.slice(1);
  const pov = seats.find((p: any) => p.GUID === NINE_SEAT_DEMO_POV_GUID);
  const folded = pov?.action === 'fold';
  if (!pov || folded || !(pov.stack > 0)) return table;

  const bb = Number(table.game.bb) || 0;
  const pot = Number(table.game.pot) || 0;
  const highestBet = Math.max(0, ...seats.map((p: any) => Number(p.lbet) || 0));
  const toCall = round2(Math.max(0, highestBet - (Number(pov.lbet) || 0)));
  const stack = Number(pov.stack);
  const facingBet = toCall > 0;
  const minRaise = round2(Math.max(bb * 2, highestBet * 2));
  const capped = (n: number) => round2(Math.min(stack, n));

  return {
    ...table,
    gameState: {
      ...table.gameState,
      pokerActions: {
        isMyTurn: String(table.game.move) === String(pov.ID),
        showAction: true,
        fold: true,
        call: facingBet,
        check: !facingBet,
        // Bet opens the round; with chips already in, it's a raise.
        bet: highestBet === 0,
        raise: highestBet > 0 && stack > toCall,
        allIn: toCall >= stack,
        preCall: facingBet,
        preCallAny: true,
        muckCard: false,
        showCard: false,
        showStraddle: false,
        playerCallAmount: capped(toCall),
        // Full shape — RaiseAction destructures betMultiplier unguarded.
        betMultiplier: {
          min: capped(minRaise),
          x2: capped(highestBet * 2 || bb * 2),
          x3: capped(highestBet * 3 || bb * 3),
          x5: capped(highestBet * 5 || bb * 5),
          oneHalf: capped(pot / 2),
          oneFourth: capped(pot / 4),
          fullPot: capped(pot),
          mraise: highestBet,
          playerTotalChips: stack,
          playersStack: stack,
          x2isDisabled: false,
          x3isDisabled: false,
          x5isDisabled: false,
          oneHalfIsDisabled: false,
          oneFourthIsDisabled: false,
          fullPotDisabled: false,
          allInDisabled: false,
          isFirstToActPostFlop: false,
          isAllIn: false,
          hasPotLimit: false
        }
      }
    }
  };
}

/** Convenience: the full 9-handed table. */
export const nineSeatTableDemo = buildTableDemo(MAX_DEMO_SEATS);

export default nineSeatTableDemo;
